import { colors } from '@lmc/common/constants'
import {
  EMAR_STOCK_SUMMARY_ITEM_ID,
  ITEM_ID_SHORT_SEIZURE
} from '@lmc/common/types'
import { Chevron, Collapse, Panel } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Switch } from 'antd'
import { chain, filter, includes } from 'lodash'
import type React from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { DotIconSection } from '../../../../components/generic'
import DotIcon from '../../../../components/generic/DotIcon/DotIcon'

type FamilyLogCategoryItemsFieldProps = {
  value?: string[]
  onChange?: (value: string[]) => void
}

// Legacy components lack TS props; type them for this file
type DotIconSectionProps = {
  text: React.ReactNode
  iconUrl: string
  iconColor: string
}
type DotIconProps = { color: string; url: string }
const DotIconSectionTyped =
  DotIconSection as React.ComponentType<DotIconSectionProps>
const DotIconTyped = DotIcon as React.ComponentType<DotIconProps>

const filterItems = (
  items: { category: string; id?: string; itemId?: string; name: string }[],
  selectedCategoryId: string,
  hideShortSeizure: boolean
) =>
  chain(items)
    .filter(
      (item) =>
        item.category === selectedCategoryId &&
        (hideShortSeizure ? item?.id !== ITEM_ID_SHORT_SEIZURE : true) &&
        item?.itemId !== EMAR_STOCK_SUMMARY_ITEM_ID
    )
    .orderBy('name')
    .value()

export function FamilyLogCategoryItemsField({
  value = [],
  onChange
}: FamilyLogCategoryItemsFieldProps) {
  const categories = useSelector((state: { data: Record<string, any[]> }) =>
    chain(state.data['log-categories'] || [])
      .orderBy('name')
      .filter(({ itemId }: { itemId?: string }) => itemId !== 'bodyMaps')
      .value()
  )
  const items = useSelector((state: { data: Record<string, any[]> }) =>
    filter(state.data['log-category-items'] || [], { visible: true })
  )

  const toggleItem = (itemId: string) => {
    if (!onChange) return
    if (includes(value, itemId)) {
      onChange(filter(value, (i) => i !== itemId))
    } else {
      onChange([...value, itemId])
    }
  }

  const isActive = (itemId: string) => includes(value, itemId)

  if (!categories?.length || !items?.length) return null

  return (
    <StyledCollapse
      expandIconPosition="end"
      expandIcon={(opt: { isActive?: boolean }) =>
        opt.isActive ? (
          <Chevron direction="down" />
        ) : (
          <Chevron direction="right" />
        )
      }
    >
      {categories.map((category: any) => {
        const categoryItems = filterItems(
          items,
          category.id,
          false /* hideShortSeizure for family portal */
        )
        if (!categoryItems.length) return null
        return (
          <Panel
            key={category.id}
            header={
              <DotIconSectionTyped
                text={<Text weight="medium">{category.name}</Text>}
                iconUrl={category.icon}
                iconColor={category.color}
              />
            }
          >
            <CategoryItemsContainer>
              {categoryItems.map((item: any) => (
                <ItemRow key={item.id}>
                  <IconContainer>
                    <DotIconTyped
                      color={
                        isActive(item.id)
                          ? item.color || category.color
                          : colors.grey
                      }
                      url={item.icon}
                    />
                  </IconContainer>
                  <ContentContainer>
                    <ItemName isActive={isActive(item.id)}>
                      {item.name}
                    </ItemName>
                    <ItemNote isActive={isActive(item.id)}>
                      {item.note}
                    </ItemNote>
                  </ContentContainer>
                  <SwitchContainer>
                    <Switch
                      onChange={() => toggleItem(item.id)}
                      checked={isActive(item.id)}
                    />
                  </SwitchContainer>
                </ItemRow>
              ))}
            </CategoryItemsContainer>
          </Panel>
        )
      })}
    </StyledCollapse>
  )
}

const StyledCollapse = styled(Collapse)`
  &&.ant-collapse.ant-collapse-icon-position-start,
  &&.ant-collapse.ant-collapse-icon-position-end {
    border-color: ${(props) => props.theme.color.neutral.color8};
  }
  .ant-collapse-item,
  .ant-collapse-header,
  .ant-collapse-content {
    border-color: ${(props) => props.theme.color.neutral.color8};
  }
  .ant-collapse-header {
    padding: 8px 16px;
  }
  .ant-collapse-header-text {
    li {
      padding-left: 0px !important;
    }
  }
  .ant-collapse-item .ant-collapse-header {
    align-items: baseline;
  }
`

const CategoryItemsContainer = styled.div`
  padding-left: 16px;
  width: 100%;
  margin-bottom: 25px;
`

const ItemRow = styled.div`
  padding-top: 8px;
  padding-bottom: 10px;
  display: flex;
`

const IconContainer = styled.div`
  flex: 1;
  max-width: 40px;
`

const ContentContainer = styled.div`
  flex: 10;
  margin-top: 6px;
  padding-left: 16px;
`

const ItemName = styled.div<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? 'black' : colors.grey)};
  font-size: 18px;
  font-weight: bold;
`

const ItemNote = styled.div<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? 'black' : colors.grey)};
`

const SwitchContainer = styled.div`
  flex: 0;
  margin: 8px 0px 0 40px;
`
